/**
 * API Client Configuration
 */
const API_BASE_URL = import.meta.env.VITE_API_URL ?? ''

/**
 * Standard API Response wrapper from backend
 */
export interface ApiResponse<T> {
  code: number
  message: string
  data: T
}

/**
 * API Error class
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
    message?: string
  ) {
    super(message ?? `API Error ${status}: ${statusText}`)
    this.name = 'ApiError'
  }
}

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('auth_token')
}

function notifyUnauthorized() {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('auth_session')
  window.dispatchEvent(new Event('auth:unauthorized'))
}

async function parseResponseBody(response: Response) {
  return response.json().catch(() => undefined)
}

function toApiError(response: Response, data?: unknown) {
  if (response.status === 401) {
    notifyUnauthorized()
  }
  return new ApiError(response.status, response.statusText, data, getApiErrorMessage(data))
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, init)
  const data = await parseResponseBody(response)
  if (!response.ok) {
    throw toApiError(response, data)
  }
  return (data as ApiResponse<T>).data
}

export function getApiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (
      error.data &&
      typeof error.data === 'object' &&
      'message' in error.data &&
      typeof error.data.message === 'string'
    ) {
      return error.data.message
    }
    return error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return '请求失败，请稍后重试'
}

/**
 * API Client with methods for different HTTP verbs
 */
export const apiClient = {
  /**
   * GET request
   */
  async get<T>(path: string): Promise<T> {
    return request<T>(path, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
      },
    })
  },

  /**
   * POST request
   */
  async post<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
      },
      body: JSON.stringify(body),
    })
  },

  async postForm<T>(path: string, body: FormData): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      headers: {
        ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
      },
      body,
    })
  },

  /**
   * PUT request
   */
  async put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
      },
      body: JSON.stringify(body),
    })
  },

  /**
   * PATCH request
   */
  async patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
      },
      body: JSON.stringify(body),
    })
  },

  /**
   * DELETE request
   */
  async delete<T>(path: string): Promise<T> {
    return request<T>(path, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
      },
    })
  },
}

export default apiClient
